import { createFeatureSelector, createSelector } from '@ngrx/store';

import { ShippingState } from './shipping.state';

import { ShippingMethod } from '@api/shipping/models/shipping.model';
import { MoneyHelpers } from '@api/util/money.helpers';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { getCurrencyInfo, getCurrencyInfoAlternates } from '@core/infrastructure/state/core.selectors';
import { ShippingPromotionsOverData } from '@features/shipping/shipping-promotions';

const selectShippingState = createFeatureSelector<ShippingState>('shipping');

export const getShippingMethods = createSelector(selectShippingState, (
    shippingState: ShippingState
) => shippingState?.values);

export const getSelectedShippingMethod = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.selectedShippingMethod
);

export const getShippingStateLoaded = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.loaded
);

export const getFreeShippingOverPromoData = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.promotions?.freeShippingOverPromoData
);

export const isCODShippingEnabled = createSelector(getShippingMethods, (shippingMethods: ShippingMethod[]) => {
    return shippingMethods.some((shippingMethod) => shippingMethod.dbcode === 'StandardCOD');
});

export const getFreeShippingPromotion = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter((promo) => promo.amount_over === 0);
    }
);

export const getFreeShippingOverPromotion = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter((promo) => promo.amount_over > 0);
    }
);

export const getFreeShippingOverAmountOver = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promos) => promos.amount_over > 0
        ).map((promo) => promo.amount_over)[0];
    }
);

export const getFreeShippingOverFSOFullPrice = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promos) => promos.amount_over > 0
        ).map((promo) => promo?.exclude_sales_items)[0];
    }
);

export const getFreeShippingOverAmountPending = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promos) => promos.amount_pending > 0
        ).map((promo) => promo.amount_pending)[0];
    }
);

export const getFreeShippingOverPromotionActive = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: ShippingPromotionsOverData) => shippingOverPromoData?.calculation_status?.global_status
);

export const getPromotionsStatus = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.promotions?.promosStatus?.status
);

export const getShippingMethodsWithPriceAlternates = (shippingMethods: ShippingMethod[]) => createSelector(
    getCurrencyInfo,
    getCurrencyInfoAlternates,
    (currencyInfo: CurrencyInfo, currencyInfoAlternates: CurrencyInfo[]) => {
        return shippingMethods.map((shippingMethod: ShippingMethod) => {
            return {
                ...shippingMethod,
                priceFormatted: MoneyHelpers.getPrimaryPriceWithAlternateFormatted(
                    shippingMethod.price,
                    currencyInfo,
                    currencyInfoAlternates
                ),
            };
        });
    }
);
